import React, { useContext, useEffect, useRef, useState } from 'react';
import { Autocomplete, Paper, PaperProps, TextField, TextFieldProps } from '@mui/material';
import {
  GooglePlacePrediction, SelectedGoogleAddress, isGoogleAddressAutocompleteConfigured,
  GOOGLE_ADDRESS_REGION_CODES, loadGoogleAddressPlaces, mapGoogleAddress,
} from '../../utils/googleAddressAutocomplete';

type Props = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value: string;
  onValueChange: (value: string) => void;
  onAddressSelect: (address: SelectedGoogleAddress) => void;
  nativeInputProps?: React.InputHTMLAttributes<HTMLInputElement>;
};
type Suggestion = { label: string; prediction: GooglePlacePrediction };

// Google's terms require the "Google Maps" attribution next to suggestions
// shown without a map. Monday 13389046002: with no suggestions (fewer than 3
// characters, loading, or no result) the list shows no attribution row.
const HasSuggestionsContext = React.createContext(false);
const AddressSuggestionsPaper = React.forwardRef<HTMLDivElement, PaperProps>((props, ref) => {
  const hasSuggestions = useContext(HasSuggestionsContext);
  return <Paper {...props} ref={ref}>
    {props.children}
    {hasSuggestions && <div style={{ padding: '10px 10px 5px' }}>
      <img src={`${process.env.PUBLIC_URL || ''}/google-maps-attribution.svg`} alt="Google Maps" height={16} />
    </div>}
  </Paper>;
});

export default function GoogleAddressTextField({ value, onValueChange, onAddressSelect, nativeInputProps, ...field }: Props) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<Suggestion[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [open, setOpen] = useState(false);
  const sequence = useRef(0);
  const session = useRef<object | undefined>(undefined);
  const mounted = useRef(true);
  const configured = isGoogleAddressAutocompleteConfigured();
  const readOnly = Boolean(nativeInputProps?.readOnly);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; sequence.current += 1; }; }, []);
  useEffect(() => {
    const request = ++sequence.current;
    setOptions([]);
    if (!configured || field.disabled || readOnly || query.trim().length < 3 || value !== query) { setBusy(false); return; }
    const timer = window.setTimeout(async () => {
      setBusy(true); setNotice('');
      try {
        const places = await loadGoogleAddressPlaces();
        if (!mounted.current || request !== sequence.current) return;
        session.current ||= new places.AutocompleteSessionToken();
        const result = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({ input: query, sessionToken: session.current, includedRegionCodes: GOOGLE_ADDRESS_REGION_CODES });
        if (!mounted.current || request !== sequence.current) return;
        const next = result.suggestions.flatMap(item => item.placePrediction
          ? [{ label: item.placePrediction.text.toString(), prediction: item.placePrediction }] : []);
        setOptions(next);
        if (!next.length) setNotice('No address found. You can enter the address manually.');
      } catch {
        if (mounted.current && request === sequence.current) setNotice('Address search is unavailable. You can enter the address manually.');
      } finally {
        if (mounted.current && request === sequence.current) setBusy(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, value, configured, field.disabled, readOnly]);

  const select = async (option: Suggestion) => {
    const request = ++sequence.current;
    session.current = undefined;
    setBusy(true); setOptions([]); setNotice('');
    try {
      const place = option.prediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents'] });
      if (!mounted.current || request !== sequence.current) return;
      const address = mapGoogleAddress(place.addressComponents);
      if (!address) { setNotice('This result has no street address. Enter the address manually.'); return; }
      // A successful selection replaces the address. Missing components stay
      // empty for review, instead of retaining parts of the previous address.
      onAddressSelect(address);
      setNotice('Check the address and add a unit number if needed.');
    } catch {
      if (mounted.current && request === sequence.current) setNotice('We could not fill in this address. Your other address fields did not change. Type the address in the fields yourself.');
    } finally {
      if (mounted.current && request === sequence.current) { setBusy(false); setQuery(''); }
    }
  };

  if (!configured) return nativeInputProps
    ? <input {...nativeInputProps} id={field.id} value={value} disabled={field.disabled} placeholder={field.placeholder}
        aria-invalid={field.error || nativeInputProps['aria-invalid']}
        onBlur={field.onBlur} onChange={event => onValueChange(event.target.value)} />
    : <TextField {...field} value={value} onChange={event => onValueChange(event.target.value)} />;
  // A free-solo list with no options would open an empty box, so the list
  // opens only while it has suggestions or is loading them.
  return <HasSuggestionsContext.Provider value={options.length > 0}><Autocomplete<Suggestion, false, true, true>
    id={field.id} freeSolo disableClearable forcePopupIcon={false}
    open={open && (options.length > 0 || busy)} onOpen={() => setOpen(true)} onClose={() => setOpen(false)}
    readOnly={readOnly}
    disabled={field.disabled} fullWidth={field.fullWidth} value="" inputValue={value}
    options={options} loading={busy} filterOptions={items => items}
    getOptionLabel={option => typeof option === 'string' ? option : option.label}
    onInputChange={(_event, next, reason) => {
      if (reason === 'input' || reason === 'clear') {
        sequence.current += 1; onValueChange(next); setQuery(next); setNotice('');
      }
    }}
    onChange={(_event, option) => { if (option && typeof option !== 'string') void select(option); }}
    renderOption={(props, option) => <li {...props} key={option.prediction.placeId}>{option.label}</li>}
    slots={{ paper: AddressSuggestionsPaper }}
    // Settings dialogs use z-index 9999. The shared suggestion portal must
    // remain above that backdrop so mouse selection works as well as keys.
    slotProps={{ listbox: { 'aria-label': 'Address suggestions' }, popper: { sx: { zIndex: 10000 } } }}
    renderInput={params => nativeInputProps ? <div ref={params.InputProps.ref}>
      <input {...nativeInputProps} {...params.inputProps}
        className={[nativeInputProps.className, params.inputProps.className].filter(Boolean).join(' ')}
        placeholder={field.placeholder}
        aria-invalid={field.error || nativeInputProps['aria-invalid']}
        aria-describedby={[nativeInputProps['aria-describedby'], notice && field.id ? `${field.id}-address-notice` : ''].filter(Boolean).join(' ') || undefined}
        onBlur={event => { params.inputProps.onBlur?.(event); field.onBlur?.(event); }} />
      {notice && <span id={field.id ? `${field.id}-address-notice` : undefined} role="status" className="mt-1 block text-xs text-gray-600">{notice}</span>}
    </div> : <TextField {...field} {...params}
      inputProps={{ ...field.inputProps, ...params.inputProps }}
      helperText={field.error ? field.helperText : [field.helperText, notice].filter(Boolean).join(' ')} />}
  /></HasSuggestionsContext.Provider>;
}
